/**
 * BookingsSection Component
 *
 * Displays booking analytics including stat cards, booking trend chart,
 * and bookings by service chart.
 */

import { Box, Typography, Grid, Paper } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import StatCard from './StatCard';
import EmptyState from './EmptyState';
import { SectionSkeleton } from './AnalyticsSkeleton';
import { ANALYTICS_SPACING } from './constants';
import { BookingStats } from '../../services/analyticsApi';

interface BookingsSectionProps {
  data: BookingStats | null;
  loading?: boolean;
}

// Colors for the horizontal bar chart
const SERVICE_COLORS = [
  '#5D5FEF', // Elevair purple
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#14B8A6', // Teal
  '#F97316', // Orange
];

/**
 * Format date for X-axis display
 */
function formatDate(value: string): string {
  const date = new Date(value);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format full date for tooltip
 */
function formatDateFull(value: string): string {
  const date = new Date(value);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Custom tooltip for area chart
 */
function AreaChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}) {
  if (active && payload && payload.length && payload[0]) {
    return (
      <Paper sx={{ p: 1.5, boxShadow: 2 }}>
        <Typography variant="body2" fontWeight={500}>
          {formatDateFull(label || '')}
        </Typography>
        <Typography variant="body2" color="primary">
          {payload[0].value} booking{payload[0].value !== 1 ? 's' : ''}
        </Typography>
      </Paper>
    );
  }
  return null;
}

/**
 * Custom tooltip for bar chart
 */
function BarChartTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: { serviceName: string; count: number } }>;
}) {
  if (active && payload && payload.length && payload[0]) {
    const data = payload[0].payload;
    return (
      <Paper sx={{ p: 1.5, boxShadow: 2 }}>
        <Typography variant="body2" fontWeight={500}>
          {data.serviceName}
        </Typography>
        <Typography variant="body2" color="primary">
          {data.count} booking{data.count !== 1 ? 's' : ''}
        </Typography>
      </Paper>
    );
  }
  return null;
}

export default function BookingsSection({ data, loading = false }: BookingsSectionProps) {
  // Show skeleton while loading
  if (loading) {
    return <SectionSkeleton />;
  }

  // Check for empty data state
  const isEmpty = !data || data.stats?.totalBookings === 0;

  if (isEmpty) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={600} sx={{ mb: ANALYTICS_SPACING.titleMarginBottom }}>
          Bookings
        </Typography>
        <EmptyState type="bookings" />
      </Box>
    );
  }

  // Transform chart data for display
  const trendData = data?.charts?.bookingTrend || [];
  const serviceData = data?.charts?.byService || [];

  // Build accessible descriptions for charts
  const trendChartDescription =
    trendData.length > 0
      ? `Booking trend chart showing ${trendData.length} data points.`
      : 'No booking trend data available';

  const serviceChartDescription =
    serviceData.length > 0
      ? `Bookings by service: ${serviceData.map((s) => `${s.serviceName}: ${s.count}`).join(', ')}`
      : 'No service data available';

  return (
    <Box>
      {/* Section Title */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: ANALYTICS_SPACING.titleMarginBottom }}>
        Bookings
      </Typography>

      {/* Stat Cards Grid */}
      <Grid container spacing={ANALYTICS_SPACING.cardGap} sx={{ mb: ANALYTICS_SPACING.cardsToChartsGap }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Bookings" value={data?.stats?.totalBookings || 0} format="number" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completion Rate"
            value={data?.stats?.completionRate || 0}
            format="percentage"
            subtitle={`${data?.stats?.completedBookings || 0} completed`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="No-Show Rate"
            value={data?.stats?.noShowRate || 0}
            format="percentage"
            subtitle={`${data?.stats?.noShowCount || 0} no-shows`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Cancelled"
            value={data?.stats?.cancelledCount || 0}
            format="number"
            subtitle="bookings cancelled"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={ANALYTICS_SPACING.chartGap}>
        {/* Booking Trend Area Chart */}
        <Grid item xs={12} lg={7}>
          <Paper
            elevation={0}
            sx={{
              p: ANALYTICS_SPACING.sectionPadding,
              height: '100%',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" fontWeight={500} sx={{ mb: 3 }}>
              Booking Trend
            </Typography>
            <Box component="span" sx={visuallyHidden}>
              {trendChartDescription}
            </Box>
            <Box sx={{ width: '100%', height: 300 }} aria-hidden="true">
              {trendData.length > 0 ? (
                <ResponsiveContainer>
                  <AreaChart data={trendData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: '#E5E7EB' }}
                      tickFormatter={formatDate}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: '#E5E7EB' }}
                    />
                    <Tooltip content={<AreaChartTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="count"
                      name="Bookings"
                      stroke="#5D5FEF"
                      fill="#5D5FEF"
                      fillOpacity={0.2}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No booking data for this period</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Bookings by Service Horizontal Bar Chart */}
        <Grid item xs={12} lg={5}>
          <Paper
            elevation={0}
            sx={{
              p: ANALYTICS_SPACING.sectionPadding,
              height: '100%',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" fontWeight={500} sx={{ mb: 3 }}>
              By Service
            </Typography>
            <Box component="span" sx={visuallyHidden}>
              {serviceChartDescription}
            </Box>
            <Box sx={{ width: '100%', height: 300 }} aria-hidden="true">
              {serviceData.length > 0 ? (
                <ResponsiveContainer>
                  <BarChart
                    data={serviceData}
                    layout="vertical"
                    margin={{ left: 20, right: 20, top: 10, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: '#E5E7EB' }}
                    />
                    <YAxis
                      type="category"
                      dataKey="serviceName"
                      tick={{ fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: '#E5E7EB' }}
                      width={120}
                    />
                    <Tooltip content={<BarChartTooltip />} />
                    <Bar dataKey="count" name="Bookings" radius={[0, 4, 4, 0]}>
                      {serviceData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={SERVICE_COLORS[index % SERVICE_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Typography color="text.secondary">No service data for this period</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
